import React from 'react';
import { ShoppingCart, X, Trash2, AlertCircle, CheckCircle } from 'lucide-react';

const fmtBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);

/**
 * SideCart — painel lateral persistente com os itens do carrinho, total ao vivo
 * e progresso do pedido mínimo. Visível em telas ≥ lg. Em telas menores
 * a `OrderSummaryBar` inferior continua sendo a UI principal.
 *
 * Props:
 *   cart: dict { [productId]: {qty, valor_unitario, produto, foto, cor?, tamanho?} }
 *   onRemove(productId), onDec(productId), onInc(productId), onCheckout()
 *   total: number
 *   itemsCount: number
 *   valorMinimo: number
 *   color: 'blue' | 'emerald'
 *   testidPrefix: string
 */
export default function SideCart({
  cart = {}, onRemove, onDec, onInc, onCheckout,
  total = 0, itemsCount = 0, valorMinimo = 0,
  color = 'blue', testidPrefix = 'side-cart',
  submitting = false, disabled = false,
}) {
  const entries = Object.entries(cart);
  const atingiu = valorMinimo <= 0 || total >= valorMinimo;
  const faltando = Math.max(0, valorMinimo - total);
  const percent = valorMinimo > 0 ? Math.min(100, Math.round((total / valorMinimo) * 100)) : 100;

  const paletteBg = color === 'emerald' ? 'from-emerald-600 to-teal-600' : 'from-blue-600 to-indigo-600';
  const paletteText = color === 'emerald' ? 'text-emerald-600' : 'text-blue-600';
  const paletteBar = color === 'emerald' ? 'bg-emerald-500' : 'bg-blue-500';

  return (
    <aside className="hidden lg:flex flex-col w-80 shrink-0 sticky top-4 self-start max-h-[calc(100vh-2rem)] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-lg overflow-hidden" data-testid={`${testidPrefix}-panel`}>
      {/* Header */}
      <div className={`px-4 py-3 bg-gradient-to-r ${paletteBg} text-white flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <ShoppingCart className="w-5 h-5" />
          <span className="font-black text-sm uppercase tracking-wider">Meu Pedido</span>
        </div>
        <span className="text-xs font-bold bg-white/20 rounded-full px-2 py-0.5" data-testid={`${testidPrefix}-count`}>
          {itemsCount} {itemsCount === 1 ? 'item' : 'itens'}
        </span>
      </div>

      {/* Items */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {entries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-gray-400">
            <ShoppingCart className="w-12 h-12 mb-2" />
            <p className="text-sm">Nenhum item adicionado</p>
            <p className="text-xs mt-1 text-center">Clique num produto para ver detalhes<br/>e adicionar ao pedido.</p>
          </div>
        ) : (
          entries.map(([id, it]) => (
            <div key={id} className="flex gap-2 items-start bg-gray-50 dark:bg-gray-900 rounded-lg p-2 group" data-testid={`${testidPrefix}-item-${id}`}>
              {it.foto ? (
                <img src={it.foto} alt="" className="w-12 h-12 rounded object-cover flex-shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded bg-gray-200 dark:bg-gray-700 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold line-clamp-2 leading-tight">{it.produto}</p>
                {(it.cor || it.tamanho) && (
                  <p className="text-[10px] text-gray-500 mt-0.5">
                    {it.cor && <span>Cor: <b>{it.cor}</b></span>}
                    {it.cor && it.tamanho && ' · '}
                    {it.tamanho && <span>Tam: <b>{it.tamanho}</b></span>}
                  </p>
                )}
                <div className="flex items-center gap-1 mt-1">
                  <button onClick={() => onDec?.(id)} className="w-5 h-5 rounded bg-gray-200 dark:bg-gray-700 text-xs font-bold hover:bg-gray-300" data-testid={`${testidPrefix}-dec-${id}`}>−</button>
                  <span className="text-xs font-bold px-1.5 tabular-nums w-6 text-center">{it.qty}</span>
                  <button onClick={() => onInc?.(id)} className={`w-5 h-5 rounded ${paletteBar} text-white text-xs font-bold hover:opacity-90`} data-testid={`${testidPrefix}-inc-${id}`}>+</button>
                  <span className={`ml-auto text-xs font-black tabular-nums ${paletteText}`}>
                    {fmtBRL(it.qty * it.valor_unitario)}
                  </span>
                </div>
              </div>
              <button
                onClick={() => onRemove?.(id)}
                className="opacity-0 group-hover:opacity-100 transition text-red-500 hover:text-red-700 p-1"
                data-testid={`${testidPrefix}-remove-${id}`}
                title="Remover"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))
        )}
      </div>

      {/* Footer com total e progresso */}
      {entries.length > 0 && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-3 space-y-2">
          {valorMinimo > 0 && (
            <div>
              <div className="h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                <div className={`${atingiu ? 'bg-emerald-500' : paletteBar} h-full transition-all duration-500`} style={{ width: `${percent}%` }} />
              </div>
              {atingiu ? (
                <p className="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold mt-1 flex items-center gap-1" data-testid={`${testidPrefix}-min-ok`}>
                  <CheckCircle className="w-3 h-3" />
                  Pedido mínimo atingido!
                </p>
              ) : (
                <p className="text-[10px] text-orange-600 dark:text-orange-400 font-bold mt-1 flex items-center gap-1" data-testid={`${testidPrefix}-min-falta`}>
                  <AlertCircle className="w-3 h-3" />
                  Faltam <b>{fmtBRL(faltando)}</b> para o mínimo de {fmtBRL(valorMinimo)}
                </p>
              )}
            </div>
          )}
          <div className="flex items-baseline justify-between">
            <span className="text-xs text-gray-500 dark:text-gray-400 uppercase font-bold">Total</span>
            <span className={`text-xl font-black tabular-nums ${paletteText}`} data-testid={`${testidPrefix}-total`}>
              {fmtBRL(total)}
            </span>
          </div>
          {onCheckout && (
            <button
              onClick={onCheckout}
              disabled={submitting || disabled || !atingiu}
              className={`w-full py-2.5 rounded-xl font-bold text-sm text-white bg-gradient-to-r ${paletteBg} shadow disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transition`}
              data-testid={`${testidPrefix}-checkout`}
            >
              {submitting ? 'Enviando...' : 'Finalizar Pedido'}
            </button>
          )}
        </div>
      )}
    </aside>
  );
}
